import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns'
import type { MyAttendanceDay } from '@/api/attendance'

const STATUS_STYLE: Record<string, { cls: string; label: string }> = {
  present: { cls: 'bg-green-100 text-green-800', label: 'Present' },
  late: { cls: 'bg-yellow-100 text-yellow-800', label: 'Late' },
  wfh: { cls: 'bg-teal-100 text-teal-800', label: 'WFH' },
  leave: { cls: 'bg-purple-100 text-purple-800', label: 'Leave' },
  absent: { cls: 'bg-red-100 text-red-700', label: 'Absent' },
  missing: { cls: 'bg-orange-100 text-orange-800', label: 'Missing' },
  holiday: { cls: 'bg-indigo-100 text-indigo-700', label: 'Holiday' },
  off: { cls: 'bg-gray-100 text-gray-400', label: 'Day off' },
}

/** Which colour a day gets: missing check-out and lateness win over plain "present". */
function dayKind(d: MyAttendanceDay, today: string): string {
  if (d.is_active && d.date < today) return 'missing'
  if (d.status === 'wfh') return 'wfh'
  if (d.status === 'leave') return 'leave'
  if (d.status === 'absent') return 'absent'
  if (d.status === 'holiday') return 'holiday'
  if (d.status === 'off') return 'off'
  return d.is_late ? 'late' : 'present'
}

/** A month grid (Mon–Sun) with one coloured cell per day from the employee's own
 *  day-by-day attendance. Days after today are left blank. */
export default function AttendanceCalendar({ month, days }: { month: Date; days: MyAttendanceDay[] }) {
  const today = format(new Date(), 'yyyy-MM-dd')
  const byDate = new Map(days.map((d) => [d.date, d]))
  const all = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) })
  const lead = (getDay(all[0]) + 6) % 7 // Monday-first grid

  return (
    <div>
      <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-medium text-gray-400 mb-1.5">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: lead }).map((_, i) => <div key={`pad-${i}`} />)}
        {all.map((date) => {
          const key = format(date, 'yyyy-MM-dd')
          const d = byDate.get(key)
          const kind = d ? dayKind(d, today) : null
          const style = kind ? STATUS_STYLE[kind] : null
          const tip = d
            ? [
                format(parseISO(key), 'EEE, d MMM'),
                style?.label,
                d.checkin_time ? `In ${format(new Date(d.checkin_time), 'h:mm a')}` : null,
                d.checkout_time ? `Out ${format(new Date(d.checkout_time), 'h:mm a')}` : null,
                d.is_late ? `${d.late_minutes}m late` : null,
              ].filter(Boolean).join(' · ')
            : undefined
          return (
            <div
              key={key}
              title={tip}
              className={`h-12 rounded-lg p-1.5 text-left text-xs ${style ? style.cls : 'bg-gray-50 text-gray-300'} ${key === today ? 'ring-2 ring-brand/50' : ''}`}
            >
              <span className="font-semibold">{format(date, 'd')}</span>
              {style && kind !== 'off' && <p className="truncate text-[10px] leading-tight mt-0.5">{style.label}</p>}
            </div>
          )
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-gray-500">
        {Object.entries(STATUS_STYLE).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded ${v.cls}`} />{v.label}</span>
        ))}
      </div>
    </div>
  )
}
